import { get, find } from "lodash";
import {
  DEFAULT_VALUES,
  RAG_THRESHOLDS,
  getScenarioTargets,
  type ScenarioTargetOverrides,
} from "src/components/shared/TeamMetrics/constants";
import {
  isSupplyHierarchySource,
  type InsightExternalManager,
  type InsightWarning,
} from "src/util/insightsHierarchyView";
import { formatRatio2, formatSignedDecimal2 } from "src/util/metricFormats";
import { isRecord } from "src/util/typeGuards";

export type { ScenarioTargetOverrides };

export type SingleStat = {
  key: string;
  max?: number;
  min?: number;
  avg?: number;
};

export type StatListItem = {
  key: string;
  samples?: number;
};

export type StatList = {
  key: string;
  items?: StatListItem[];
};

export type ValueListItem = {
  key?: string;
  label?: string;
  value?: number;
  comparisonValue?: number;
  group?: { name?: string };
  optionalLabel?: string;
};

export type ValueList = {
  key: string;
  items?: ValueListItem[];
};

export type RoleCounts = {
  managers?: number | null;
  individualContributors?: number | null;
  unassigned?: number | null;
};

export type InsightsHierarchyConfig = {
  layerRootMode?: string | null;
  layerRootOffset?: number | null;
  layerIndexOrigin?: string | null;
  depthMeasure?: string | null;
  ownerMemberMode?: string | null;
};

export type Insights = {
  hierarchySource?: string | null;
  singleStats?: SingleStat[];
  statLists?: StatList[];
  valueLists?: ValueList[];
  total?: number;
  roleCounts?: RoleCounts | null;
  /** Population-pure denominator from the supply overlay (D2); prefer over total. */
  memberHeadcount?: number | null;
  /** Sum of FTE over the same population as memberHeadcount (supply overlay). */
  memberFteSum?: number | null;
  ownershipCoverage?: number | null;
  externalManagers?: InsightExternalManager[] | null;
  warnings?: InsightWarning[] | null;
  supplyStatsTruncated?: boolean | null;
  insightsHierarchy?: InsightsHierarchyConfig | null;
};

export type RagStatus = "green" | "amber" | "red" | "grey";
type RagType = "layers" | "span" | "ratio" | "overhead" | "headcount";
export type SpanRange = { min: number; max: number };

export const isRagStatus = (value: unknown): value is RagStatus =>
  value === "green" || value === "amber" || value === "red" || value === "grey";

export type LayersMetricOptions = {
  teamDepthFromTop?: number | undefined;
  hierarchyView?: string | null | undefined;
  targetsByScenario?: ScenarioTargetOverrides | undefined;
};

export type LayersMetricResult = {
  current: number | null;
  target: number;
  delta: number | null;
  deltaFormatted: string;
  ragStatus: RagStatus;
  teamDepthFromTop: number;
  scenarioTarget: number;
};

export type SpanMetricResult = {
  current: number | null;
  target: SpanRange;
  targetFormatted?: string;
  delta: number | null;
  deltaFormatted: string;
  ragStatus: RagStatus;
};

export type ManagerToICRatioMetricResult = {
  current: number | null;
  currentFormatted: string;
  target: number;
  targetFormatted: string;
  delta: number | null;
  deltaFormatted: string;
  ragStatus: RagStatus;
  managers?: number;
  ics?: number;
  isEstimated?: boolean;
  unassigned?: number | null;
};

const round = (value: number | null | undefined, decimals = 1): number => {
  if (value === null || value === undefined || isNaN(value)) {
    return 0;
  }
  return Number(value.toFixed(decimals));
};

/**
 * Calculate RAG (Red/Amber/Green) status based on value and target
 */
export const getRAGStatus = (
  value: number | null | undefined,
  target: number | SpanRange,
  type: RagType
): RagStatus => {
  if (value === null || value === undefined || isNaN(value)) {
    return "grey"; // No data
  }

  switch (type) {
    case "layers": {
      if (typeof target !== "number") {
        return "grey";
      }
      if (value <= target) {
        return "green";
      }
      if (value === target + 1) {
        return "amber";
      }
      return "red";
    }
    case "span": {
      if (typeof target !== "object" || target === null) {
        return "grey";
      }
      const { min, max } = target;
      if (value >= min && value <= max) {
        return "green";
      }
      const range = max - min;
      const tolerance = RAG_THRESHOLDS.SPAN_RANGE_TOLERANCE;
      const lowerBound = min - range * tolerance;
      const upperBound = max + range * tolerance;
      if (value >= lowerBound && value <= upperBound) {
        return "amber";
      }
      return "red";
    }
    case "ratio": {
      if (typeof target !== "number") {
        return "grey";
      }
      if (value >= target) {
        return "green";
      }
      const threshold = target * RAG_THRESHOLDS.RATIO_AMBER_THRESHOLD;
      if (value >= threshold) {
        return "amber";
      }
      return "red";
    }
    case "overhead": {
      if (typeof target !== "number") {
        return "grey";
      }
      if (value <= target) {
        return "green";
      }
      const threshold = target * RAG_THRESHOLDS.OVERHEAD_AMBER_MULTIPLIER;
      if (value <= threshold) {
        return "amber";
      }
      return "red";
    }
    case "headcount": {
      if (typeof target !== "number") {
        return "grey";
      }
      if (value <= target) {
        return "green";
      }
      const threshold = target * RAG_THRESHOLDS.HEADCOUNT_AMBER_MULTIPLIER;
      if (value <= threshold) {
        return "amber";
      }
      return "red";
    }
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
};

/**
 * Calculate team's depth from the top of the organization
 */
export const calculateTeamDepthFromTop = (team: unknown): number => {
  if (!isRecord(team)) {
    return 0;
  }

  // Try hierarchyIds first (array of parent IDs)
  if (Array.isArray(team["hierarchyIds"])) {
    // hierarchyIds includes the team itself, so depth is length - 1
    return Math.max(0, team["hierarchyIds"].length - 1);
  }

  // Try parentIds (array of parent IDs)
  if (Array.isArray(team["parentIds"])) {
    // parentIds doesn't include the team itself, so depth is length
    return team["parentIds"].length;
  }

  // Try hierarchy string (e.g., "CEO > Division > Team")
  if (typeof team["hierarchy"] === "string") {
    const HIERARCHY_SEPARATOR = " > ";
    const parts = team["hierarchy"].split(HIERARCHY_SEPARATOR);
    // hierarchy includes the team itself, so depth is length - 1
    return Math.max(0, parts.length - 1);
  }

  // Default: assume top level if no hierarchy info
  return 0;
};

/**
 * Calculate layers metric with target, delta, and RAG status
 */
export const calculateLayersMetric = (
  insights: Insights,
  targetLayersOrScenario: number | string = DEFAULT_VALUES.MAX_LAYERS,
  options: LayersMetricOptions = {}
): LayersMetricResult => {
  const { teamDepthFromTop = 0, hierarchyView, targetsByScenario } = options;

  // If scenario string provided, get targets from scenario
  const scenarioTargetLayers =
    typeof targetLayersOrScenario === "string"
      ? getScenarioTargets(targetLayersOrScenario, targetsByScenario).layers.max
      : targetLayersOrScenario;

  const isSupply = isSupplyHierarchySource(
    resolveMetricLens(insights, hierarchyView)
  );

  // reporting_line: subtract teamDepthFromTop from target.
  // supply: use scenarioTargetLayers as-is (only Math.max(1, …)).
  const adjustedTargetLayers = isSupply
    ? Math.max(1, scenarioTargetLayers)
    : Math.max(1, scenarioTargetLayers - teamDepthFromTop);

  const layersStats = get(insights, "singleStats", []);
  const layersStat = find(layersStats, { key: "layers" });

  if (!layersStat) {
    return {
      current: null,
      target: adjustedTargetLayers,
      delta: null,
      deltaFormatted: "N/A",
      ragStatus: "grey",
      teamDepthFromTop,
      scenarioTarget: scenarioTargetLayers,
    };
  }

  // Org-unit (supply) Insights already return team-relative (or configured-root)
  // depths — do not also subtract group hierarchyIds depth.
  // reporting_line (people/manager) still uses the HRIS layers-from-CEO adjustment.
  const layersFromApex = layersStat.max || 0;
  const current = isSupply
    ? Math.max(0, layersFromApex)
    : Math.max(0, layersFromApex - teamDepthFromTop);
  const delta = current - adjustedTargetLayers;
  const deltaFormatted =
    delta === 0 ? "0" : delta > 0 ? `+${delta}` : `${delta}`;
  const ragStatus = getRAGStatus(current, adjustedTargetLayers, "layers");

  return {
    current,
    target: adjustedTargetLayers,
    delta,
    deltaFormatted,
    ragStatus,
    teamDepthFromTop,
    scenarioTarget: scenarioTargetLayers,
  };
};

/** Span metric with scenario target range, delta, and RAG status. */
export const calculateSpanMetric = (
  insights: Insights,
  scenario: string = "standard",
  targetsByScenario?: ScenarioTargetOverrides
): SpanMetricResult => {
  const scenarioTargets = getScenarioTargets(scenario, targetsByScenario);
  const targetSpanMin = scenarioTargets.span.min;
  const finalTargetSpanMax = scenarioTargets.span.max;
  const spanStats = get(insights, "singleStats", []);
  const spanStat = find(spanStats, { key: "span" });

  if (!spanStat) {
    return {
      current: null,
      target: { min: targetSpanMin, max: finalTargetSpanMax },
      delta: null,
      deltaFormatted: "N/A",
      ragStatus: "grey",
    };
  }

  const current = round(spanStat.avg || 0, 2);
  const targetRange = { min: targetSpanMin, max: finalTargetSpanMax };

  let delta: number | null = null;
  let deltaFormatted = "N/A";
  if (current < targetSpanMin) {
    delta = current - targetSpanMin;
    deltaFormatted = formatSignedDecimal2(delta);
  } else if (current > finalTargetSpanMax) {
    delta = current - finalTargetSpanMax;
    deltaFormatted = formatSignedDecimal2(delta);
  } else {
    delta = 0;
    deltaFormatted = formatSignedDecimal2(0);
  }

  const ragStatus = getRAGStatus(current, targetRange, "span");

  return {
    current,
    target: targetRange,
    targetFormatted: `${targetSpanMin}-${finalTargetSpanMax}`,
    delta: round(delta, 2),
    deltaFormatted,
    ragStatus,
  };
};

/**
 * The lens the D13 "never estimate on supply" guard should judge against.
 * Prefer the user's selected hierarchyView (matches the query lens); fall back
 * to the payload's hierarchySource only when no view was requested. Prevents
 * estimating on the supply lens while hierarchySource is still loading/stale.
 * The single source of this rule — used by every metric calculator here and
 * the ManagerToICRatio widget.
 */
const resolveMetricLens = (
  insights: Insights | null | undefined,
  hierarchyView?: string | null
): string | null | undefined =>
  hierarchyView != null && hierarchyView !== ""
    ? hierarchyView
    : get(insights, "hierarchySource");

type MetricLensOptions = { hierarchyView?: string | null | undefined };

/**
 * Calculate manager-to-IC ratio metric with target, delta, and RAG status
 */
export const calculateManagerToICRatioMetric = (
  insights: Insights,
  targetRatioMinOrScenario:
    | number
    | string = DEFAULT_VALUES.MIN_MANAGER_TO_IC_RATIO,
  targetsByScenario?: ScenarioTargetOverrides,
  options?: MetricLensOptions
): ManagerToICRatioMetricResult => {
  // If scenario string provided, get targets from scenario
  const targetRatioMin =
    typeof targetRatioMinOrScenario === "string"
      ? getScenarioTargets(targetRatioMinOrScenario, targetsByScenario)
          .managerToICRatio.min
      : targetRatioMinOrScenario;
  // Population-pure denominator when the supply overlay ran (D2); legacy total otherwise.
  const memberHeadcount = get(insights, "memberHeadcount", null);
  const total =
    memberHeadcount != null ? memberHeadcount : get(insights, "total", 0);
  const spanStats = get(insights, "singleStats", []);
  const spanStat = find(spanStats, { key: "span" });
  const roleCounts = get(insights, "roleCounts");
  // D13: in OD (supply lens) never estimate — exact roleCounts or nothing.
  const isSupplyLens =
    resolveMetricLens(insights, options?.hierarchyView) === "supply";
  const hasExactCounts = roleCounts && typeof roleCounts.managers === "number";

  if (
    (!spanStat && !roleCounts) ||
    !total ||
    (isSupplyLens && !hasExactCounts)
  ) {
    return {
      current: null,
      currentFormatted: "N/A",
      target: targetRatioMin,
      targetFormatted: `1:${Math.round(1 / targetRatioMin)}`,
      delta: null,
      deltaFormatted: "N/A",
      ragStatus: "grey",
    };
  }

  let managers: number;
  let ics: number;
  let isEstimated = true;
  if (hasExactCounts) {
    // hasExactCounts guarantees managers is a number; ?? 0 is for the narrower.
    managers = Math.max(0, roleCounts.managers ?? 0);
    ics = Math.max(0, roleCounts.individualContributors ?? 0);
    isEstimated = false;
  } else {
    // Legacy fallback when API has not shipped roleCounts yet
    const avgSpan = spanStat?.avg || 0;
    managers = avgSpan > 0 ? Math.round(total / (avgSpan + 1)) : 0;
    ics = Math.max(0, total - managers);
  }

  // Calculate ratio (ICs per manager) - rounded to 2 decimal places
  const currentRatio = managers > 0 && ics > 0 ? round(ics / managers, 2) : 0;
  const currentFormatted =
    managers > 0 && ics > 0 ? formatRatio2(currentRatio) : "N/A";

  // Calculate delta (difference from target ratio)
  const delta = currentRatio - 1 / targetRatioMin; // Convert target to ICs per manager
  const deltaFormatted = formatSignedDecimal2(round(delta, 2));

  const ragStatus = getRAGStatus(currentRatio, 1 / targetRatioMin, "ratio");

  return {
    current: currentRatio,
    currentFormatted,
    target: targetRatioMin,
    targetFormatted: `1:${Math.round(1 / targetRatioMin)}`,
    delta,
    deltaFormatted,
    ragStatus,
    managers,
    ics,
    isEstimated,
    unassigned: roleCounts?.unassigned ?? null,
  };
};

export const RAG_TO_TONE: Record<RagStatus, string> = {
  green: "success",
  amber: "warning",
  red: "danger",
  grey: "muted",
};

export const ragStatusToTone = (ragStatus: unknown): string =>
  isRagStatus(ragStatus) ? RAG_TO_TONE[ragStatus] : RAG_TO_TONE.grey;
